<template>
  <div class="record-box">
    <!-- 原音播放、录音、我的录音区域 -->
    <template v-if="!isRecording && !scoreLoading">
      <!-- 播放原音 -->
      <div class="record-box_item">
        <img
          v-if="pause"
          class="icon-1"
          :src="imageMap.audio_play"
          alt="播放原音"
          @click="handlePlay"
        />

        <div class="play-progress" v-else>
          <svg class="w-full h-full" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              :stroke="circlrTrack[theme]"
              stroke-width="8"
            />
            <circle
              ref="progressRingRef"
              cx="50"
              cy="50"
              r="44"
              fill="none"
              :stroke="circlrThumb[theme]"
              stroke-width="8"
              stroke-linecap="round"
              transform="rotate(-90 50 50)"
              stroke-dasharray="276.5"
              stroke-dashoffset="0"
              class="progress-transition"
            />
          </svg>
          <button id="play-pause-btn" @click="pauseAudio">
            <img :src="imageMap.icon_pause" alt="暂停" />
          </button>
        </div>
        <span>播放原音</span>
      </div>

      <!-- 点击录音 -->
      <div class="record-box_item" @click="handleRecording">
        <img class="icon-2" src="@/assets/listening/icon-record.svg" alt="录音" />
        <span>点击录音</span>
      </div>

      <!-- 我的录音 -->
      <div class="record-box_item">
        <template v-if="!props.overall && props.overall !== 0">
          <img class="icon-3" src="@/assets/listening/icon-my-record.svg" alt="我的录音" />
        </template>
        <template v-else>
          <div class="score-box" @click="playMyAudio">
            <img
              ref="myAudioImgRef"
              class="icon-3"
              :src="imageMap.play2"
              alt="播放我的录音"
            />
            <div class="score-box_tag">{{ props.overall }}</div>
          </div>
        </template>
        <span>我的录音</span>
      </div>
    </template>

    <!-- 录音中状态 -->
    <template v-if="isRecording">
      <div>
        <div class="recording-box">
          <div class="recording-animate">
            <SoundWave />
          </div>
          <img
            class="icon-send"
            src="@/assets/listening/icon-send.svg"
            alt="停止录音"
            @click.stop="stopRecord"
          />
        </div>
        <div class="text-center" style="color: #6a6a6a; font-size: 0.7rem">正在录音...</div>
      </div>
    </template>

    <!-- 评分加载状态 -->
    <template v-if="scoreLoading">
      <div class="loader-container">
        <div class="dot-box">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
        <div>AI正在评分中</div>
      </div>
    </template>

    <!-- 音频元素 -->
    <audio ref="audioPlayerRef" :src="audioUrl" @timeupdate="updateTimeDisplay" @ended="handleAudioEnded" @pause="handleAudioPaused" />
    <audio ref="myAudioPlayerRef" :src="props.myAudioUrl" @ended="handleMyAudioEnded" @pause="handleMyAudioPaused" />
  </div>
</template>

<script setup>
import Html5Recorder from "chivox_h5sdk/src/html5/html5recorder";
import SoundWave from "./SoundWave.vue";
import { nextTick, onMounted, ref, defineProps, computed, watch, reactive, onBeforeUnmount } from "vue";
import http from "@/js/http";
import { useListenConfigStore } from "@/hooks/useListenConfigStore";
import { imageConfig } from "@/views/listening/components/ThemeTag/imageConfig";
import { useRoute } from "vue-router";
import { openMsgToast } from "@/views/AIGenerate/openModal";
import store from "@/js/store";

// 状态管理
const listenStore = useListenConfigStore();

// 计算属性
const imageMap = computed(() => imageConfig[theme.value]);
const theme = computed(() => listenStore.theme);
const recorder = computed(() => listenStore.recorder);
const coreType = computed(() => props.refText.includes(" ") ? "en.sent.score" : "en.word.score");

// 属性定义
const props = defineProps({
  refText: {
    type: String,
    required: true
  },
  overall: {
    type: [Number, String],
    default: null
  },
  myAudioUrl: {
    type: String,
    default: ''
  },
  aduioSrc: {
    type: String,
    default: ''
  }
});

// 常量定义
const OSS_URL = `${store.getOEMBASE() || ""}grammar_content/`;
const RADIUS = 44;
const CIRCUMFERENCE = RADIUS * 2 * Math.PI;

// 响应式变量
const audioUrl = ref("");
const pause = ref(true);
const isRecording = ref(false);
const scoreLoading = ref(false);
const audioTimer = ref(null);

// 引用
const progressRingRef = ref();
const audioPlayerRef = ref();
const myAudioPlayerRef = ref();
const myAudioImgRef = ref();

// 事件发射
const $emit = defineEmits("finishScore");

// 监听音频源变化
watch(
  () => props.aduioSrc,
  (newVal) => {
    if (newVal) {
      audioUrl.value = `${OSS_URL}${newVal}`;
    }
  },
  { immediate: true }
);

// 监听播放速率变化
watch(
  () => listenStore.playbackRate,
  (rate) => {
    if (audioPlayerRef.value) audioPlayerRef.value.playbackRate = rate;
    if (myAudioPlayerRef.value) myAudioPlayerRef.value.playbackRate = rate;
  }
);

// 监听音量变化
watch(
  () => listenStore.volume,
  (volume) => {
    const volumeValue = parseInt(volume) / 100;
    if (audioPlayerRef.value) audioPlayerRef.value.volume = volumeValue;
    if (myAudioPlayerRef.value) myAudioPlayerRef.value.volume = volumeValue;
  }
);

// 生命周期
onMounted(() => {
  initPlayAudio();
});

onBeforeUnmount(() => {
  // 清理定时器和事件监听
  clearInterval(audioTimer.value);
  if (audioPlayerRef.value) {
    audioPlayerRef.value.removeEventListener('timeupdate', updateTimeDisplay);
    audioPlayerRef.value.removeEventListener('ended', handleAudioEnded);
    audioPlayerRef.value.removeEventListener('pause', handleAudioPaused);
  }
  if (myAudioPlayerRef.value) {
    myAudioPlayerRef.value.removeEventListener('ended', handleMyAudioEnded);
    myAudioPlayerRef.value.removeEventListener('pause', handleMyAudioPaused);
  }
});

// 原音播放控制
const handlePlay = () => {
  pause.value = false;
  nextTick(() => {
    audioPlayerRef.value.play().then(() => {
      audioPlayerRef.value.playbackRate = listenStore.playbackRate;
      audioPlayerRef.value.volume = parseInt(listenStore.volume) / 100;
      myAudioPlayerRef.value?.pause();
    }).catch(err => {
      console.error('播放失败:', err);
      pause.value = true;
    });
  });
};

const pauseAudio = () => {
  audioPlayerRef.value?.pause();
};

const initPlayAudio = () => {
  nextTick(() => {
    if (progressRingRef.value) {
      progressRingRef.value.style.strokeDasharray = `${CIRCUMFERENCE} ${CIRCUMFERENCE}`;
      progressRingRef.value.style.strokeDashoffset = CIRCUMFERENCE;
    }
  });
};

const updateTimeDisplay = () => {
  if (!audioPlayerRef.value?.duration) return;
  const percent = (audioPlayerRef.value.currentTime / audioPlayerRef.value.duration) * 100;
  setProgress(percent);
};

const setProgress = (percent) => {
  const offset = CIRCUMFERENCE - (percent / 100) * CIRCUMFERENCE;
  nextTick(() => {
    if (progressRingRef.value) {
      progressRingRef.value.style.strokeDashoffset = offset;
    }
  });
};

const handleAudioEnded = () => {
  setProgress(0);
  pause.value = true;
};

const handleAudioPaused = () => {
  pause.value = true;
};

// 录音控制
const handleRecording = () => {
  if (isRecording.value || scoreLoading.value) {
    openMsgToast("当前正在录音评分，请勿频繁操作");
    return;
  }

  listenStore.resetRecord();
  resetMyAudio();
  resetAudio();

  recorder.value.record({
    duration: 40000,
    audioType: "wav",
    serverParams: {
      coreType: coreType.value,
      refText: props.refText,
      rank: 100,
      attachAudioUrl: 1,
    },
    onRecordIdGenerated: (tokenId) => {
      console.info("录音ID生成:", tokenId);
    },
    onStart: () => {
      isRecording.value = true;
    },
    onStop: () => {
      isRecording.value = false;
      scoreLoading.value = true;
    },
    onScore: (score) => {
      console.info("评分结束:", score);
      scoreLoading.value = false;
      // 处理音频URL
      score.audioUrl = `https://${score.audioUrl.replace(":8002", "")}.mp3`;
      $emit("finishScore", score);
    },
    onScoreError: (err) => {
      console.error("评分错误:", err);
      scoreLoading.value = false;
      if (err.error?.msg === "unauthorized: record concurrency is full") {
        openMsgToast('并发使用已超限,请稍后重试');
      }
    },
  });
};

const stopRecord = () => {
  recorder.value.stopRecord();
};

// 我的录音控制
const playMyAudio = () => {
  if (!props.myAudioUrl) return;

  resetMyAudio();
  let num = 0;

  setTimeout(() => {
    myAudioPlayerRef.value.play().then(() => {
      myAudioPlayerRef.value.playbackRate = listenStore.playbackRate;
      myAudioPlayerRef.value.volume = parseInt(listenStore.volume) / 100;
      audioPlayerRef.value?.pause();

      audioTimer.value = setInterval(() => {
        if (myAudioImgRef.value) {
          myAudioImgRef.value.src = ++num % 2 ? imageMap.value.play1 : imageMap.value.play2;
        }
      }, 300);
    }).catch(err => {
      console.error('播放我的录音失败:', err);
    });
  }, 200);
};

const handleMyAudioEnded = () => {
  resetMyAudio();
};

const handleMyAudioPaused = () => {
  resetMyAudio();
};

// 重置方法
const resetAudio = () => {
  if (audioPlayerRef.value) {
    audioPlayerRef.value.pause();
    audioPlayerRef.value.currentTime = 0;
  }
  setProgress(0);
  pause.value = true;
};

const resetMyAudio = () => {
  if (myAudioPlayerRef.value) {
    myAudioPlayerRef.value.pause();
    myAudioPlayerRef.value.currentTime = 0;
  }
  clearInterval(audioTimer.value);
  audioTimer.value = null;
  if (myAudioImgRef.value) {
    myAudioImgRef.value.src = imageMap.value.play2;
  }
};

const resetRecord = () => {
  listenStore.resetRecord();
  isRecording.value = false;
  scoreLoading.value = false;
};

// 样式相关
const circlrThumb = {
  green: "#04976B",
  brown: "#B35F34",
  blue: "#007AFF",
  white: "#0DCB8B",
  dark: "#DFBF98",
};

const circlrTrack = {
  green: "rgba(4, 151, 107, 0.30)",
  brown: "rgba(179, 95, 52, 0.2)",
  blue: "#E5F2FF",
  white: "rgba(16, 194, 139, 0.3)",
  dark: "rgba(223, 191, 152, 0.3)",
};

// 暴露方法
defineExpose({
  resetMyAudio,
  resetAudio,
  resetRecord
});
</script>

<style scoped lang="less">
.record-box {
  display: flex;
  justify-content: center;
  gap: 2.8rem;
  background-color: var(--page-inner-bg);

  &_item {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 0.7rem;
    color: var(--record-text);
  }

  .icon-1,
  .icon-3 {
    width: 1.5rem;
    height: 1.5rem;
    margin-bottom: 0.38rem;
  }

  .icon-2 {
    width: 2rem;
    height: 2rem;
    margin-bottom: 0.57rem;
  }

  .score-box {
    position: relative;

    &_tag {
      background: #ff8c00;
      color: white;
      width: 1.6rem;
      height: 1.1rem;
      border: 1px solid #ffffff;
      text-align: center;
      line-height: 1rem;
      border-radius: 1rem 1rem 1rem 0;
      position: absolute;
      top: -0.5rem;
      right: -1.3rem;
    }
  }
}

.recording-box {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;

  .recording-animate {
    width: 14rem;
    height: 2rem;
    background-color: var(--recording-animate-bg);
    border-radius: 2.3rem;
    position: relative;
  }

  .icon-send {
    width: 2rem;
    height: 2rem;
  }
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6a6a6a;
  font-size: 0.7rem;

  .dot-box {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.32rem;
    margin-bottom: 0.45rem;
  }

  .dot {
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    animation: colorChange 1.5s infinite ease-in-out;
  }

  .dot:nth-child(1) {
    animation-delay: 0s;
  }

  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes colorChange {
    0%,
    100% {
      background-color: #ff8c00; /* primary */
      transform: scale(1);
    }
    50% {
      background-color: rgba(255, 140, 0, 0.2); /* accent */
      transform: scale(1.2);
    }
  }
}

.play-progress {
  width: 1.5rem;
  height: 1.5rem;
  position: relative;
  margin-bottom: 0.38rem;
}

.progress-transition {
  transition: stroke-dashoffset 0.1s linear;
}

#play-pause-btn {
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  img {
    width: 0.5rem;
    height: 0.7rem;
  }
}
</style>